/**
 * TeamMember Web Component
 * Click a card to expand it in place to 4x area, revealing extended bio + Q&A.
 * Only one card per section may be expanded at a time.
 *
 * Closing:
 *  - Click the close button in the expanded panel
 *  - Click outside the expanded card
 *  - Press Escape
 */
class TeamMember extends HTMLElement {
  constructor() {
    super();

    this.compact = this.querySelector('.team-member__compact');
    this.expanded = this.querySelector('.team-member__expanded');
    this.closeBtn = this.querySelector('.team-member__close');

    /* If there's no extra content, the card is non-interactive */
    if (!this.compact || !this.expanded) return;

    this.bindEvents();
  }

  bindEvents() {
    this.compact.addEventListener('click', this.handleCardClick.bind(this));
    this.closeBtn?.addEventListener('click', this.handleCloseClick.bind(this));
  }

  handleCardClick(event) {
    if (this.classList.contains('is-expanded')) return;
    event.preventDefault();
    this.expand();
  }

  handleCloseClick(event) {
    event.stopPropagation();
    this.collapse({ returnFocus: true });
  }

  expand() {
    /* Collapse any currently-expanded sibling within the same section grid */
    const grid = this.closest('.team-section__grid');
    if (grid) {
      grid.querySelectorAll('team-member.is-expanded').forEach((other) => {
        if (other !== this) other.collapse({ returnFocus: false });
      });
    }

    this.classList.add('is-expanded');
    this.compact.setAttribute('aria-expanded', 'true');

    /* Reset detail-panel scroll to top each time we open */
    const detail = this.querySelector('.team-member__detail');
    if (detail) detail.scrollTop = 0;

    /* Bind document listeners only while expanded */
    this._onDocClick = this.handleDocumentClick.bind(this);
    this._onKeydown = this.handleKeydown.bind(this);
    document.addEventListener('click', this._onDocClick);
    document.addEventListener('keydown', this._onKeydown);

    /* Move focus to the close button so screen-reader/keyboard users land here */
    requestAnimationFrame(() => {
      this.closeBtn?.focus({ preventScroll: false });
    });
  }

  collapse({ returnFocus = false } = {}) {
    if (!this.classList.contains('is-expanded')) return;

    this.classList.remove('is-expanded');
    this.compact.setAttribute('aria-expanded', 'false');

    if (this._onDocClick) {
      document.removeEventListener('click', this._onDocClick);
      this._onDocClick = null;
    }
    if (this._onKeydown) {
      document.removeEventListener('keydown', this._onKeydown);
      this._onKeydown = null;
    }

    if (returnFocus) {
      this.compact.focus({ preventScroll: true });
    }
  }

  handleDocumentClick(event) {
    if (!this.contains(event.target)) {
      this.collapse({ returnFocus: false });
    }
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.collapse({ returnFocus: true });
    }
  }
}

customElements.define('team-member', TeamMember);
